import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import { Input } from "@components/ui/input";
import { Search } from "lucide-react";
import {
  codeNodes,
  customNodes,
  dataTransmission,
  logicNodes,
  lsNodes,
} from "./common/nodeTemplates";
import { GeneralContext } from "@services/providers/GeneralProvider";

interface NodeSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNodeSelect: (nodeType: string, nodeTemplate: any) => void;
  activeMeshFlowType?: string;
}

const NodeSearchDialog: React.FC<NodeSearchDialogProps> = ({
  open,
  onOpenChange,
  onNodeSelect,
  activeMeshFlowType,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);
  const { isAdminUser } = React.useContext(GeneralContext);

  const hasNodePermission = (node: any) => {
    if (!node?.permission) {
      return true;
    }

    if (node.permission === "isAdmin") {
      return isAdminUser;
    }

    if (node.permission === "isUser") {
      return !isAdminUser;
    }

    return true;
  };

  // Reset search when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setFocusedIndex(0);
    }
  }, [open]);

  // Reset focused index when search results change
  useEffect(() => {
    setFocusedIndex(0);
  }, [searchQuery]);

  // Combine all node templates
  const allNodes = useMemo(
    () => [
      ...customNodes
        .filter((node) => hasNodePermission(node))
        .map((node) => ({ ...node, category: "Action Nodes" })),
      ...logicNodes
        .filter((node) => hasNodePermission(node))
        .map((node) => ({ ...node, category: "Logic Nodes" })),
      ...lsNodes
        .filter((node) => hasNodePermission(node))
        .map((node) => ({ ...node, category: "Linkshadow Nodes" })),
      ...codeNodes
        .filter((node) => hasNodePermission(node))
        .map((node) => ({ ...node, category: "Code Nodes" })),
      ...dataTransmission
        .filter((node) => hasNodePermission(node))
        .map((node) => ({
          ...node,
          category: "Data Transmission",
        })),
    ],
    [isAdminUser],
  );

  // Filter nodes based on search query
  const filteredNodes = useMemo(() => {
    if (!searchQuery.trim()) {
      return allNodes;
    }

    const query = searchQuery.toLowerCase();
    return allNodes.filter(
      (node) =>
        node.header.toLowerCase().includes(query) ||
        node.type.toLowerCase().includes(query) ||
        node.category.toLowerCase().includes(query),
    );
  }, [searchQuery, allNodes]);

  // Group filtered nodes by category
  const groupedNodes = useMemo(() => {
    const groups: Record<string, any[]> = {};

    filteredNodes.forEach((node) => {
      if (!groups[node.category]) {
        groups[node.category] = [];
      }
      groups[node.category].push(node);
    });

    return groups;
  }, [filteredNodes]);

  const handleNodeSelect = (node: any) => {
    // Check if workflow is threatlookup and prevent Save Logs/Save to Datalist nodes
    if (activeMeshFlowType === "threatlookup") {
      if (node.type === "savelogs" || node.type === "savedatalist") {
        return; // Skip these nodes for threatlookup workflows
      }
    }

    onNodeSelect(node.type, node);
    onOpenChange(false);
  };

  // Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const totalNodes = filteredNodes.length;
      if (totalNodes === 0) return;

      switch (e.key) {
        case "ArrowDown":
        case "ArrowRight":
          e.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % totalNodes);
          break;

        case "ArrowUp":
        case "ArrowLeft":
          e.preventDefault();
          setFocusedIndex((prev) => (prev - 1 + totalNodes) % totalNodes);
          break;

        case "Enter": {
          e.preventDefault();
          const focusedNode = filteredNodes[focusedIndex];
          if (focusedNode) {
            handleNodeSelect(focusedNode);
          }
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, filteredNodes, focusedIndex]);

  const renderNodeItem = (node: any, index: number) => {
    const Icon = node.icon;
    const isDisabled =
      activeMeshFlowType === "threatlookup" &&
      (node.type === "savelogs" || node.type === "savedatalist");
    const isFocused = index === focusedIndex;

    return (
      <button
        key={node.type}
        onClick={() => !isDisabled && handleNodeSelect(node)}
        disabled={isDisabled}
        className={`group relative flex flex-col items-center justify-center gap-2 p-4 w-[130px]
                   rounded-2xl border backdrop-blur-md
                   transition-all duration-300

                   ${
                     isDisabled
                       ? "opacity-40 cursor-not-allowed"
                       : isFocused
                         ? `ring-2 ring-foreground -translate-y-1 shadow-xl scale-[1.03]`
                         : "hover:-translate-y-1 hover:shadow-xl hover:scale-[1.03] active:scale-[0.98] cursor-pointer"
                   }`}
        style={{
          borderColor: node.borderColor,
          boxShadow: isFocused
            ? "0 4px 12px 0 rgba(188, 188, 188, 0.9)"
            : `0 0 0 1px ${node.shadowColor}`,
        }}
      >
        {/* Glow background */}
        {!isDisabled && (
          <div
            className={`absolute inset-0 rounded-2xl transition ${
              isFocused ? "opacity-100" : "opacity-0 group-hover:opacity-100"
            }`}
          />
        )}

        <Icon
          color={node.borderColor}
          size={26}
          className={`relative z-10 transition-transform ${
            !isDisabled && (isFocused ? "scale-110" : "group-hover:scale-110")
          } ${node.header == "If Else" ? "rotate-90" : ""}`}
        />

        <div
          className="text-xs font-semibold text-center relative z-10"
          style={{ color: node.borderColor }}
        >
          {node.header}
        </div>
      </button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col
                   bg-gradient-to-b from-background/95 to-background
                   backdrop-blur-xl !rounded-2xl"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Search Nodes</DialogTitle>
        </DialogHeader>

        <div className="border p-4 rounded-2xl">
          {/* Search Input */}
          <div
            className="
                      flex items-center gap-1
                      border border-border shadow-md
                      rounded-2xl px-3 mb-4 mt-1 
                    "
          >
            <Search size={18} className="text-muted-foreground" />

            <Input
              placeholder="Search by node name, type, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="
                          border-0
                          focus:ring-0
                          focus:outline-none
                          focus-visible:ring-0
                          focus-visible:ring-offset-0
                          !rounded-0
                        "
              autoFocus
            />
          </div>

          {/* Nodes Grid */}
          <div className="flex-1 overflow-y-auto p-3 space-y-6">
            {Object.keys(groupedNodes).length === 0 ? (
              <div className="text-center text-xs py-8 text-muted-foreground">
                No nodes found matching {searchQuery ? `"${searchQuery}"` : ""}
              </div>
            ) : (
              (() => {
                let globalIndex = 0;
                return Object.entries(groupedNodes).map(([category, nodes]) => (
                  <div key={category}>
                    {/* <h3 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                  {category}
                  <span className="ml-2 text-xs font-normal">
                    ({nodes.length})
                  </span>
                </h3> */}

                    <div className="flex flex-wrap gap-4">
                      {nodes.map((node) => {
                        const index = globalIndex++;
                        return renderNodeItem(node, index);
                      })}
                    </div>
                  </div>
                ));
              })()
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NodeSearchDialog;
